/**
 * PriceRangeSlider Component
 *
 * Dual-handle slider for filtering properties by price range.
 * Includes preset buttons and text inputs for precise control.
 */

import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import InputAdornment from "@mui/material/InputAdornment";
import { useState, useCallback } from "react";

const MIN_PRICE = 0;
const MAX_PRICE = 5_000_000;
const STEP = 50_000;

interface PricePreset {
  label: string;
  min: number | null;
  max: number | null;
}

const PRICE_PRESETS: PricePreset[] = [
  { label: "Any", min: null, max: null },
  { label: "Under $500k", min: null, max: 500_000 },
  { label: "$500k - $1M", min: 500_000, max: 1_000_000 },
  { label: "$1M - $2M", min: 1_000_000, max: 2_000_000 },
  { label: "$2M+", min: 2_000_000, max: null },
];

export interface PriceRangeSliderProps {
  minValue: number | null;
  maxValue: number | null;
  onChange: (min: number | null, max: number | null) => void;
  disabled?: boolean;
}

function formatPrice(value: number): string {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}M`;
  }
  return `$${(value / 1000).toFixed(0)}k`;
}

function formatPriceInput(value: number | null): string {
  if (value === null) return "";
  return value.toLocaleString();
}

function parsePriceInput(value: string): number | null {
  const cleaned = value.replace(/[^0-9]/g, "");
  if (!cleaned) return null;
  return parseInt(cleaned, 10);
}

export function PriceRangeSlider({
  minValue,
  maxValue,
  onChange,
  disabled = false,
}: PriceRangeSliderProps) {
  // Local state for dragging slider and editing inputs
  const [isDragging, setIsDragging] = useState(false);
  const [dragValue, setDragValue] = useState<[number, number]>([
    MIN_PRICE,
    MAX_PRICE,
  ]);
  const [isEditingMin, setIsEditingMin] = useState(false);
  const [isEditingMax, setIsEditingMax] = useState(false);
  const [minInputValue, setMinInputValue] = useState("");
  const [maxInputValue, setMaxInputValue] = useState("");

  // Slider value: use drag value while dragging, otherwise derive from props
  const sliderValue: [number, number] = isDragging
    ? dragValue
    : [minValue ?? MIN_PRICE, maxValue ?? MAX_PRICE];

  // Input display values: use local state while editing, otherwise derive from props
  const minInputDisplay = isEditingMin
    ? minInputValue
    : formatPriceInput(minValue);
  const maxInputDisplay = isEditingMax
    ? maxInputValue
    : formatPriceInput(maxValue);

  const handleSliderChange = useCallback(
    (_event: Event, newValue: number | number[]) => {
      setIsDragging(true);
      setDragValue(newValue as [number, number]);
    },
    [],
  );

  const handleSliderCommit = useCallback(
    (_event: Event | React.SyntheticEvent, newValue: number | number[]) => {
      setIsDragging(false);
      const [min, max] = newValue as [number, number];
      onChange(min === MIN_PRICE ? null : min, max === MAX_PRICE ? null : max);
    },
    [onChange],
  );

  const handleMinInputFocus = useCallback(() => {
    setIsEditingMin(true);
    setMinInputValue(formatPriceInput(minValue));
  }, [minValue]);

  const handleMaxInputFocus = useCallback(() => {
    setIsEditingMax(true);
    setMaxInputValue(formatPriceInput(maxValue));
  }, [maxValue]);

  const handleMinInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMinInputValue(e.target.value);
    },
    [],
  );

  const handleMaxInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMaxInputValue(e.target.value);
    },
    [],
  );

  const handleMinInputBlur = useCallback(() => {
    setIsEditingMin(false);
    const parsed = parsePriceInput(minInputValue);
    const currentMax = maxValue ?? MAX_PRICE;
    const newMin =
      parsed === null
        ? null
        : Math.max(MIN_PRICE, Math.min(parsed, currentMax));
    onChange(newMin, maxValue);
  }, [minInputValue, maxValue, onChange]);

  const handleMaxInputBlur = useCallback(() => {
    setIsEditingMax(false);
    const parsed = parsePriceInput(maxInputValue);
    const currentMin = minValue ?? MIN_PRICE;
    const newMax =
      parsed === null
        ? null
        : Math.min(MAX_PRICE, Math.max(parsed, currentMin));
    onChange(minValue, newMax);
  }, [maxInputValue, minValue, onChange]);

  const handlePresetClick = useCallback(
    (preset: PricePreset) => {
      onChange(preset.min, preset.max);
    },
    [onChange],
  );

  const isPresetActive = (preset: PricePreset) => {
    return preset.min === minValue && preset.max === maxValue;
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Price Range
      </Typography>

      {/* Preset chips */}
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        sx={{ mb: 2, gap: 0.5 }}
      >
        {PRICE_PRESETS.map((preset) => (
          <Chip
            key={preset.label}
            label={preset.label}
            onClick={() => handlePresetClick(preset)}
            color={isPresetActive(preset) ? "primary" : "default"}
            variant={isPresetActive(preset) ? "filled" : "outlined"}
            size="small"
            disabled={disabled}
          />
        ))}
      </Stack>

      {/* Dual-handle slider */}
      <Box sx={{ px: 1 }}>
        <Slider
          value={sliderValue}
          onChange={handleSliderChange}
          onChangeCommitted={handleSliderCommit}
          valueLabelDisplay="auto"
          valueLabelFormat={formatPrice}
          min={MIN_PRICE}
          max={MAX_PRICE}
          step={STEP}
          disabled={disabled}
          sx={{
            mt: 1,
            // Touch-friendly thumb size on mobile (48px touch target)
            "& .MuiSlider-thumb": {
              width: { xs: 28, md: 20 },
              height: { xs: 28, md: 20 },
            },
            // Increase track height on mobile for easier touch
            "& .MuiSlider-track, & .MuiSlider-rail": {
              height: { xs: 6, md: 4 },
            },
          }}
        />
      </Box>

      {/* Min/Max text inputs */}
      <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
        <TextField
          label="Min Price"
          value={minInputDisplay}
          onFocus={handleMinInputFocus}
          onChange={handleMinInputChange}
          onBlur={handleMinInputBlur}
          size="small"
          fullWidth
          disabled={disabled}
          placeholder="No minimum"
          InputProps={{
            startAdornment: minInputDisplay ? (
              <InputAdornment position="start">$</InputAdornment>
            ) : null,
          }}
        />
        <TextField
          label="Max Price"
          value={maxInputDisplay}
          onFocus={handleMaxInputFocus}
          onChange={handleMaxInputChange}
          onBlur={handleMaxInputBlur}
          size="small"
          fullWidth
          disabled={disabled}
          placeholder="No maximum"
          InputProps={{
            startAdornment: maxInputDisplay ? (
              <InputAdornment position="start">$</InputAdornment>
            ) : null,
          }}
        />
      </Stack>
    </Box>
  );
}
